import React from 'react';

const SectionHeader = ({ num, tag, title, color = '#F97316' }) => (
  <div style={{ marginBottom: '36px', position: 'relative' }}>
    <div
      style={{
        fontFamily: 'Impact, sans-serif',
        fontSize: 'clamp(4rem, 10vw, 8rem)',
        fontWeight: 900,
        color: `${color}08`,
        lineHeight: 1,
        position: 'absolute',
        top: '-20px',
        left: '-10px',
        userSelect: 'none',
        letterSpacing: '-0.04em'
      }}
    >
      {num}
    </div>
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          color: color,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}
      >
        {tag}
      </div>
      <h2
        style={{
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(1.5rem, 3.5vw, 2.75rem)',
          fontWeight: 900,
          color: '#EEF0F2',
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          lineHeight: 1
        }}
      >
        {title}
      </h2>
    </div>
  </div>
);

const BodyText = ({ children }) => (
  <p
    style={{
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      color: 'rgba(238,240,242,0.72)',
      lineHeight: 1.85,
      marginBottom: '20px',
      maxWidth: '760px'
    }}
  >
    {children}
  </p>
);

const Divider = ({ color = '#F97316' }) => (
  <div style={{ margin: '48px 0', display: 'flex', alignItems: 'center', gap: '16px' }}>
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
    <div style={{ width: '8px', height: '8px', background: color, transform: 'rotate(45deg)' }} />
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
  </div>
);

const PaymentIPSection = () => {
  return (
    <>
      {/* Section 5: Payment */}
      <section id="betaling" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="05"
          tag="§ 5 — BETALINGSBETINGELSER"
          title="Betalingsbetingelser"
          color="#EF4444"
        />

        <BodyText>
          Alle betalinger behandles sikkert gjennom Stripe. NordPass lagrer ikke kortinformasjon direkte — all betalingsinformasjon tokeniseres og håndteres av vår betalingsleverandør i samsvar med PCI DSS-standarder.
        </BodyText>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '28px' }}>
          {[
            { method: 'Visa / Mastercard', icon: '💳', desc: 'Alle norske og internasjonale kort' },
            { method: 'Vipps', icon: '📱', desc: 'Norsk mobilbetaling' },
            { method: 'Bankoverføring', icon: '🏦', desc: 'Norske bankkontoer (IBAN)' },
            { method: 'Faktura', icon: '📄', desc: 'Kun for bedriftskunder (Premium)' },
          ]?.map((m, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{m?.icon}</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2', marginBottom: '4px' }}>{m?.method}</div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: 'rgba(138,149,163,0.6)' }}>{m?.desc}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderLeft: '4px solid #EF4444',
            padding: '24px',
            marginBottom: '24px'
          }}
        >
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#EF4444', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Mislighold & Inkasso</div>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.9375rem', color: 'rgba(238,240,242,0.72)', lineHeight: 1.75, margin: 0 }}>
            Ved manglende betaling vil tjenesten suspenderes etter 7 dager. Etter 30 dager kan utestående beløp oversendes til inkasso i henhold til norsk inkassolovgivning. Purregebyr og inkassokostnader vil tilkomme i henhold til gjeldende satser.
          </p>
        </div>

        <BodyText>
          Alle priser er oppgitt i norske kroner (NOK). For bedriftskunder faktureres MVA separat. Prisendringer varsles med minimum 30 dagers skriftlig varsel.
        </BodyText>
      </section>

      <Divider color="#EF4444" />

      {/* Section 6: Intellectual Property */}
      <section id="immaterialrett" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="06"
          tag="§ 6 — IMMATERIELLE RETTIGHETER"
          title="Immaterielle rettigheter"
          color="#F97316"
        />

        <BodyText>
          Alt innhold på NordPass-plattformen, inkludert men ikke begrenset til programvare, design, tekst, grafikk, logoer, ikoner, bilder, lyd, og datasamlinger, er beskyttet av norsk og internasjonal opphavsrettslovgivning og eies av NordPass AS.
        </BodyText>

        <div
          style={{
            background: 'rgba(249,115,22,0.06)',
            border: '1px solid rgba(249,115,22,0.2)',
            padding: '28px',
            marginBottom: '28px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              fontFamily: 'Impact, sans-serif',
              fontSize: '6rem',
              color: 'rgba(249,115,22,0.05)',
              lineHeight: 1,
              userSelect: 'none'
            }}
          >
            ©
          </div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#F97316', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>Tillatt bruk</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              'Personlig, ikke-kommersiell bruk av tjenestene',
              'Nedlasting av data for personlig bruk (ikke videredistribusjon)',
              'Deling av lenker til NordPass-innhold med kreditering',
            ]?.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ color: '#10B981', fontSize: '1rem', lineHeight: 1.4 }}>✓</span>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: 'rgba(238,240,242,0.72)', lineHeight: 1.6 }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '16px 0' }} />
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#EF4444', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Forbudt bruk</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              'Kopiering, reproduksjon eller distribusjon av innhold uten tillatelse',
              'Reverse engineering av programvare eller API-er',
              'Kommersiell bruk av NordPass-data uten lisensavtale',
              'Bruk av NordPass-merkevare uten skriftlig tillatelse',
            ]?.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ color: '#EF4444', fontSize: '1rem', lineHeight: 1.4 }}>✗</span>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: 'rgba(238,240,242,0.72)', lineHeight: 1.6 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <BodyText>
          Brudd på disse rettighetene kan medføre erstatningskrav og strafferettslig forfølgelse i henhold til norsk åndsverkslov og straffelov.
        </BodyText>
      </section>
    </>
  );
};

export default PaymentIPSection;
